import { ChangeDetectionStrategy, Component, effect, inject, input, signal } from '@angular/core';
import { ConnectionService } from '@services/connection-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { EventLoggerService } from '@services/event-logger-service';

export enum ImprovementTimelinePlacement {
  REPORT = 'REPORT',
  REGIMEN = 'REGIMEN',
}

@Component({
  selector: 'app-improvement-timeline',
  templateUrl: './improvement-timeline.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImprovementTimelineComponent {
  private readonly conn = inject(ConnectionService);
  private readonly experimentService = inject(ExperimentService);
  private readonly eventLogger = inject(EventLoggerService);

  placement = input.required<ImprovementTimelinePlacement>();
  regimenClass = input.required<string>();

  readonly timelineImage = signal<string | null>(null);

  constructor() {
    effect((): void => {
      const regimenClass = this.regimenClass();
      this.timelineImage.set(null);
      if (!this.showTimeline() || !regimenClass) return;
      void this.loadTimelineImage(regimenClass);
    });
  }

  private async loadTimelineImage(regimenClass: string): Promise<void> {
    const timeline = await this.conn.getImprovementTimeline(regimenClass);
    if (this.regimenClass() !== regimenClass) return;
    if (timeline?.timelineImage) {
      this.timelineImage.set(timeline.timelineImage);
      this.logImpression(regimenClass);
    }
  }

  private showTimeline(): boolean {
    return this.placement() === ImprovementTimelinePlacement.REPORT
      ? this.experimentService.showImprovementTimelineOnReport
      : this.experimentService.showImprovementTimelineOnRegimen;
  }

  private logImpression(regimenClass: string): void {
    this.eventLogger.cleverTapEvent('impression', JSON.stringify({
      name: 'improvement_timeline_shown',
      placement: this.placement(),
      class: regimenClass,
    }));
  }
}
